/**
 * Notification Service for better user experience
 * Provides toast-like notifications instead of browser alerts
 */

class NotificationService {
  constructor() {
    this.notifications = []
    this.container = null
    this.init()
  }

  init() {
    // Create notification container if it doesn't exist
    if (!document.getElementById('notification-container')) {
      this.container = document.createElement('div')
      this.container.id = 'notification-container'
      this.container.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 9999;
        max-width: 400px;
      `
      document.body.appendChild(this.container)
    } else {
      this.container = document.getElementById('notification-container')
    }
  }

  /**
   * Show a notification
   * @param {string} message - The message to display
   * @param {string} type - Type of notification (error, warning, success, info)
   * @param {number} duration - Duration in milliseconds (default: 5000)
   */
  show(message, type = 'info', duration = 5000) {
    const notification = this.createNotification(message, type)
    this.container.appendChild(notification)
    
    // Auto remove after duration
    setTimeout(() => {
      this.remove(notification)
    }, duration)

    // Add click to dismiss
    notification.addEventListener('click', () => {
      this.remove(notification)
    })

    return notification
  }

  /**
   * Create notification element
   * @param {string} message - The message to display
   * @param {string} type - Type of notification
   * @returns {HTMLElement} - The notification element
   */
  createNotification(message, type) {
    const notification = document.createElement('div')
    notification.className = `notification notification-${type}`
    
    const colors = {
      error: '#dc3545',
      warning: '#ffc107',
      success: '#28a745',
      info: '#17a2b8'
    }

    const icons = {
      error: '⚠️',
      warning: '⚠️',
      success: '✅',
      info: 'ℹ️'
    }

    notification.style.cssText = `
      background: white;
      border-left: 4px solid ${colors[type] || colors.info};
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      padding: 15px 20px;
      margin-bottom: 10px;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;
    `

    notification.innerHTML = `
      <div style="display: flex; align-items: flex-start;">
        <span style="margin-right: 10px; font-size: 16px;">${icons[type] || icons.info}</span>
        <div style="flex: 1; word-wrap: break-word;">
          <div style="font-weight: 500; margin-bottom: 5px; color: #333;">
            ${this.getTitle(type)}
          </div>
          <div style="color: #666; font-size: 14px; line-height: 1.4;">
            ${message.replace(/\n/g, '<br>')}
          </div>
        </div>
        <button style="
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          color: #999;
          margin-left: 10px;
          padding: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        " onclick="this.parentElement.parentElement.parentElement.remove()">×</button>
      </div>
    `

    // Add slide-in animation
    setTimeout(() => {
      notification.style.transform = 'translateX(0)'
    }, 10)

    return notification
  }

  /**
   * Get title for notification type
   * @param {string} type - Notification type
   * @returns {string} - Title
   */
  getTitle(type) {
    const titles = {
      error: 'Error',
      warning: 'Warning',
      success: 'Success',
      info: 'Information'
    }
    return titles[type] || titles.info
  }

  /**
   * Remove notification
   * @param {HTMLElement} notification - The notification element to remove
   */
  remove(notification) {
    if (notification && notification.parentNode) {
      notification.style.transform = 'translateX(100%)'
      notification.style.opacity = '0'
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification)
        }
      }, 300)
    }
  }

  /**
   * Clear all notifications
   */
  clear() {
    if (this.container) {
      this.container.innerHTML = ''
    }
  }

  // Convenience methods
  error(message, duration) {
    return this.show(message, 'error', duration)
  }

  warning(message, duration) {
    return this.show(message, 'warning', duration)
  }

  success(message, duration) {
    return this.show(message, 'success', duration)
  }

  info(message, duration) {
    return this.show(message, 'info', duration)
  }
}

// Create singleton instance
const notificationService = new NotificationService()

export default notificationService
